import { PlusOutlined } from "@ant-design/icons";
import { Button as AntButton, Col, Row, Tooltip, Typography, Radio } from "antd";
import React, { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { InputRadio } from "../../../../../atoms/FormElement";
import { InputNumberFieldV2 as InputNumberField } from "../../../../../atoms/FormElement/InputNumberField";
import { getValidationProps } from "../../../../../libs/form/utils/getValidationProps";
import { getMinErrorMessage } from "../../../../../libs/form/utils/getMinErrorMessage";
import DeleteIcon from "../../../../../../assets/svg/bookingform/deleteIcon";
import { TOR_POLICY_OPTIONS } from "./constants";
import type { TorRulesFormFieldsProps, TorRulesFormValues } from "./types";
import { useTorRulesFieldArray } from "./useTorRulesFieldArray";

const { Text } = Typography;

export const TorRulesFormFields = (props: TorRulesFormFieldsProps) => {
  const { disabled = false, maxRules, onRegisterCopy } = props;
  const {
    control: contextControl,
    formState: { errors: contextErrors },
    setValue,
    clearErrors,
  } = useFormContext<TorRulesFormValues>();

  const control = props.control ?? contextControl;
  const errors = props.errors ?? contextErrors;

  const { fields, addRule, removeRule, replace, canAddRule, canRemoveRule } = useTorRulesFieldArray({
    control,
    maxRules,
  });

  useEffect(() => {
    if (!onRegisterCopy) return;

    onRegisterCopy((values) => {
      setValue("torRuleCondition", values.torRuleCondition, { shouldValidate: false });
      replace(values.torRules);
      clearErrors();
    });
  }, [clearErrors, onRegisterCopy, replace, setValue]);

  const torRulesValues = useWatch({
    control,
    name: "torRules",
  });

  return (
    <>
      <Row gutter={24} style={{ marginTop: "30px" }}>
        <Col span={24}>
          <AntButton
            type="dashed"
            disabled={disabled || !canAddRule}
            htmlType="button"
            icon={<PlusOutlined />}
            block
            onClick={addRule}
          >
            <Text className="ag-primary fontWeight600" style={{ fontSize: "14px" }}>
              Add Another Rule
            </Text>
          </AntButton>
        </Col>
      </Row>

      <Row style={{ marginTop: "30px" }}>
        <Col span={24}>
          <InputRadio
            disabled={disabled}
            control={control}
            fieldname="torRuleCondition"
            label="Select TOR Policy"
            options={
              <Row gutter={[16, 24]}>
                {TOR_POLICY_OPTIONS.map((option, i) => (
                  <Col span={8} key={`${option.value}-${i}`}>
                    <Radio value={option.value}>{option.label}</Radio>
                  </Col>
                ))}
              </Row>
            }
          />
        </Col>
      </Row>

      {fields.map((field, index) => (
        <Row key={field.id} gutter={[6, 12]}>
          <Col span={4}>
            <InputNumberField
              isRequired
              fieldname={`torRules.${index}.torPercent`}
              label="TOR %"
              control={control}
              inputNumberProps={{
                placeholder: "TOR %",
                min: 0,
                max: 100,
                precision: 3,   // e.g. 5.252%
                disabled,
              }}
              {...getValidationProps({
                fieldName: "torPercent",
                errors: errors?.torRules?.[index],
                isRequired: true,
              })}
            />
          </Col>
          <Col span={5}>
            <InputNumberField
              isRequired
              fieldname={`torRules.${index}.minSale`}
              label="Min Sale"
              control={control}
              format="currency"
              inputNumberProps={{
                placeholder: "Min Sale",
                min: 0,
                disabled,
              }}
              {...getValidationProps({
                fieldName: "minSale",
                errors: errors?.torRules?.[index],
                isRequired: true,
              })}
            />
          </Col>
          <Col span={5}>
            <InputNumberField
              isRequired
              fieldname={`torRules.${index}.maxSale`}
              label="Max Sale"
              control={control}
              format="currency"
              inputNumberProps={{
                placeholder: "Max Sale",
                disabled,
              }}
              rules={{
                ...getValidationProps({
                  fieldName: "maxSale",
                  errors: errors?.torRules?.[index],
                  isRequired: true,
                }).rules,
                validate: (value) => {
                  const minSale = Number(torRulesValues?.[index]?.minSale ?? 0);
                  if (value == null) return true;
                  if (Number(value) < minSale) return getMinErrorMessage("maxSale", "greater", minSale);
                  return true;
                },
              }}
            />
          </Col>
          <Col span={4}>
            <InputNumberField
              isRequired
              fieldname={`torRules.${index}.minLimit`}
              label="Min Limit"
              control={control}
              format="currency"
              inputNumberProps={{
                placeholder: "Min Limit",
                min: 0,
                disabled,
              }}
              {...getValidationProps({
                fieldName: "minLimit",
                errors: errors?.torRules?.[index],
                isRequired: true,
              })}
            />
          </Col>
          <Col span={5}>
            <InputNumberField
              isRequired
              fieldname={`torRules.${index}.uptoLimit`}
              label="Upto Limit"
              control={control}
              format="currency"
              inputNumberProps={{
                placeholder: "Upto Limit",
                disabled,
              }}
              rules={{
                ...getValidationProps({
                  fieldName: "uptoLimit",
                  errors: errors?.torRules?.[index],
                  isRequired: true,
                }).rules,
                validate: (value) => {
                  const minLimit = Number(torRulesValues?.[index]?.minLimit ?? 0);
                  if (value == null) return true;
                  if (Number(value) < minLimit) return getMinErrorMessage("uptoLimit", "greater", minLimit);
                  return true;
                },
              }}
            />
          </Col>
          <Col span={1} style={{ marginTop: "30px" }}>
            <Tooltip title="Delete" trigger="hover" showArrow={false}>
              <AntButton
                type="link"
                disabled={disabled || !canRemoveRule}
                style={{ padding: "0px" }}
                onClick={() => removeRule(index)}
              >
                <DeleteIcon />
              </AntButton>
            </Tooltip>
          </Col>
        </Row>
      ))}
    </>
  );
};
